import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Plus, Trash2 } from "lucide-react";

import { ChalkProgressBar } from "../components/chalk/primitives";
import { ChalkLogoLockup } from "../components/chalk/Logo";
import { api } from "../lib/chalkApi";
import { useTeam } from "../contexts/TeamContext";
import { toast } from "sonner";

const SPORTS = [
  { id: "baseball", label: "Baseball" },
  { id: "softball", label: "Softball" },
  { id: "soccer", label: "Soccer" },
  { id: "basketball", label: "Basketball" },
  { id: "football", label: "Football" },
  { id: "volleyball", label: "Volleyball" },
  { id: "other", label: "Other" },
];

const TZ_OPTIONS = [
  "America/Los_Angeles", "America/Denver", "America/Chicago",
  "America/New_York", "America/Phoenix", "America/Anchorage", "Pacific/Honolulu",
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useTeam();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [sport, setSport] = useState("baseball");
  const [tz, setTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles");
  const [seasonLabel, setSeasonLabel] = useState("Spring 2026");
  const [athletes, setAthletes] = useState([{ _k: 1, first_name: "", last_name: "", guardian_email: "" }]);
  const [teamId, setTeamId] = useState(null);
  const [busy, setBusy] = useState(false);
  const nextKey = useRef(2);

  const STEPS = 5;
  const progress = (step - 1) / STEPS;

  function addAthlete() {
    setAthletes((rows) => [...rows, { _k: nextKey.current++, first_name: "", last_name: "", guardian_email: "" }]);
  }
  function updateAthlete(i, patch) {
    setAthletes((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function removeAthlete(i) {
    setAthletes((rows) => rows.filter((_, idx) => idx !== i));
  }

  async function createTeam() {
    setBusy(true);
    try {
      const data = await api.createTeam({ name, sport, timezone: tz, season_label: seasonLabel });
      setTeamId(data.team.id);
      setStep(3);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function importRoster() {
    setBusy(true);
    try {
      const cleaned = athletes
        .map((r) => ({ ...r, first_name: r.first_name.trim(), last_name: r.last_name.trim() }))
        .filter((r) => r.first_name && r.last_name);
      if (cleaned.length === 0) {
        setStep(4);
        return;
      }
      await api.importRoster(teamId, cleaned);
      setStep(4);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    await refresh();
    navigate("/app");
  }

  return (
    <div className="chalk-min-h-screen" style={{ background: "var(--chalk-bg)", display: "flex", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 520, padding: "32px 20px 80px" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 22 }}>
          <ChalkLogoLockup size={26} color="var(--chalk-board)" />
        </div>
        <ChalkProgressBar progress={progress} />

        <div className="chalk-fadeup" style={{ marginTop: 28 }}>
          {step === 1 && (
            <StepCard
              title="What are you coaching?"
              subtitle="We'll set the right defaults for your sport."
              cta="Next"
              onNext={() => name.trim() && setStep(2)}
              disabled={!name.trim()}
              testId="onboarding-step1"
            >
              <Label>Team name</Label>
              <input data-testid="onboarding-team-name" className="chalk-input" style={{ paddingLeft: 14 }} value={name} onChange={(e) => setName(e.target.value)} placeholder="Reno Reds Baseball" />
              <Label className="mt-4">Sport</Label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {SPORTS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`sport-${s.id}`}
                    onClick={() => setSport(s.id)}
                    style={{
                      padding: "10px 12px", textAlign: "left",
                      background: sport === s.id ? "#E1F5EE" : "#fff",
                      border: `0.5px solid ${sport === s.id ? "var(--chalk-green)" : "var(--chalk-border)"}`,
                      borderRadius: 10, cursor: "pointer", fontSize: 14, color: "var(--chalk-board)", fontWeight: 500,
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </StepCard>
          )}

          {step === 2 && (
            <StepCard
              title="Where do you play?"
              subtitle="Pick your timezone — we'll handle the rest."
              cta={busy ? "Creating…" : "Create team"}
              onNext={createTeam}
              onBack={() => setStep(1)}
              disabled={busy}
              testId="onboarding-step2"
            >
              <Label>Timezone</Label>
              <select className="chalk-input" style={{ paddingLeft: 14 }} value={tz} onChange={(e) => setTz(e.target.value)} data-testid="onboarding-tz">
                {TZ_OPTIONS.map((z) => <option key={z}>{z}</option>)}
              </select>
              <Label className="mt-4">Season label</Label>
              <input data-testid="onboarding-season" className="chalk-input" style={{ paddingLeft: 14 }} value={seasonLabel} onChange={(e) => setSeasonLabel(e.target.value)} />
            </StepCard>
          )}

          {step === 3 && (
            <StepCard
              title="Add your roster."
              subtitle="First name is fine. Guardian email lets us send their Clipboard invite."
              cta={busy ? "Saving…" : "Save roster"}
              onNext={importRoster}
              onBack={() => setStep(2)}
              disabled={busy}
              testId="onboarding-step3"
            >
              <div style={{ display: "grid", gap: 8 }}>
                {athletes.map((r, i) => (
                  <div key={r._k} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 36px", gap: 6 }}>
                    <input className="chalk-input" style={{ paddingLeft: 14 }} placeholder="First" value={r.first_name} onChange={(e) => updateAthlete(i, { first_name: e.target.value })} data-testid={`athlete-${i}-first`} />
                    <input className="chalk-input" style={{ paddingLeft: 14 }} placeholder="Last" value={r.last_name} onChange={(e) => updateAthlete(i, { last_name: e.target.value })} data-testid={`athlete-${i}-last`} />
                    <input className="chalk-input" style={{ paddingLeft: 14 }} placeholder="Guardian email" value={r.guardian_email} onChange={(e) => updateAthlete(i, { guardian_email: e.target.value })} data-testid={`athlete-${i}-email`} />
                    <button onClick={() => removeAthlete(i)} aria-label="Remove" style={{ background: "transparent", border: "none", color: "var(--chalk-muted)", cursor: "pointer" }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={addAthlete} className="chalk-btn-ghost" style={{ marginTop: 12 }} data-testid="add-athlete">
                <Plus size={16} aria-hidden="true" /> Add another player
              </button>
            </StepCard>
          )}

          {step === 4 && (
            <StepCard
              title="Almost there."
              subtitle="We'll send invites to every guardian email when you finish."
              cta="Continue"
              onNext={() => setStep(5)}
              onBack={() => setStep(3)}
              testId="onboarding-step4"
            >
              <div className="chalk-card">
                <div style={{ fontSize: 15, fontWeight: 500, color: "var(--chalk-board)" }}>Quick recap</div>
                <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 6, fontSize: 14, color: "var(--chalk-muted)" }}>
                  <li>Team: <span style={{ color: "var(--chalk-board)" }}>{name}</span></li>
                  <li>Sport: <span style={{ color: "var(--chalk-board)" }}>{sport}</span></li>
                  <li>Timezone: <span style={{ color: "var(--chalk-board)" }}>{tz}</span></li>
                  <li>Players: <span style={{ color: "var(--chalk-board)" }}>{athletes.filter((r) => r.first_name && r.last_name).length}</span></li>
                </ul>
              </div>
            </StepCard>
          )}

          {step === 5 && (
            <StepCard
              title="Welcome to ChalkOS, Coach."
              subtitle="Your Chalkboard is ready."
              cta={"Send invites and open my Chalkboard"}
              onNext={finish}
              testId="onboarding-step5"
            >
              <div className="chalk-card">
                <div style={{ fontSize: 14, color: "var(--chalk-muted)" }}>
                  Tip: install ChalkOS to your home screen for one-tap access during practice.
                </div>
              </div>
            </StepCard>
          )}
        </div>
      </div>
    </div>
  );
}

function StepCard({ title, subtitle, cta, onNext, onBack, children, disabled, testId }) {
  return (
    <div data-testid={testId}>
      <h2 className="font-serif-display" style={{ fontSize: 24, margin: 0, color: "var(--chalk-board)" }}>{title}</h2>
      {subtitle && <p style={{ fontSize: 14, color: "var(--chalk-muted)", marginTop: 6 }}>{subtitle}</p>}
      <div style={{ marginTop: 18, display: "grid", gap: 12 }}>{children}</div>
      <div style={{ marginTop: 22, display: "flex", gap: 10 }}>
        {onBack && <button onClick={onBack} className="chalk-btn-ghost" data-testid="onboarding-back">Back</button>}
        <button onClick={onNext} disabled={disabled} className="chalk-btn-primary" data-testid="onboarding-next">
          {cta} <ArrowRight size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function Label({ children, className = "" }) {
  return <div className={className} style={{ fontSize: 12, color: "var(--chalk-muted)", marginBottom: 6 }}>{children}</div>;
}
